import React, { useEffect, useState } from "react";
import "../../assets/css/reportRegistrations.css";
import { getToken } from "../auth/authConfig";
import AppButton from "../layout/AppButton";

const initialFormData = {
  reportId: "",
  groupId: "",
  datasetId: "",
  rlsEnabled: "false",
  reportName: "",
  reportUrl: "",
  reportDescription: "",
};

const ReportRegistrationsFrom = ({ editReportData, onClearEdit, onReportRegistered }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const passtoken = localStorage.getItem("oidcToken");
  const baseUrl = process.env.REACT_APP_SERVER_URL;

  useEffect(() => {
    if (!editReportData) {
      return;
    }

    const prefilledData = {
      ...initialFormData,
      ...editReportData,
      reportUrl: `https://app.powerbi.com/reportEmbed?reportId=${editReportData.reportId || ""}&groupId=${editReportData.groupId || ""}`,
      rlsEnabled:
        editReportData.rlsEnabled === true || editReportData.rlsEnabled === "true"
          ? "true"
          : "false",
    };

    setFormData(prefilledData);
    setIsSubmitted(false);
  }, [editReportData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => {
      const updatedData = { ...prevData, [name]: value };
      const generatedUrl = `https://app.powerbi.com/reportEmbed?reportId=${updatedData.reportId}&groupId=${updatedData.groupId}`;
      return { ...updatedData, reportUrl: generatedUrl };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccess(false);
    setLoading(true);

    try {
      const payload = {
        report_guid: formData.reportId,
        report_name: formData.reportName,
        group_id: formData.groupId,
        dataset_id: formData.datasetId,
        url: formData.reportUrl,
        rls_enabled: formData.rlsEnabled === "true",
        report_description: formData.reportDescription,
        report_id: editReportData?.reportId || null,
      };

      const endpoint = editReportData
        ? `${baseUrl}/api/reports/${formData.reportId}`
        : `${baseUrl}/api/reports/register`;

      const method = editReportData ? "PUT" : "POST";

      const response = await fetch(endpoint, {
        method,
        headers: {
          "oidc-token": await getToken(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${editReportData ? "update" : "register"} report`);
      }

      const data = await response.json();
      setSuccess(true);
      setIsSubmitted(true);

      // If callback provided, invoke it with the new report data
      if (onReportRegistered) {
        onReportRegistered(data.data ?? data);
      }

      // Reset form after successful submission
      setTimeout(() => {
        handleReset();
      }, 1500);
    } catch (err) {
      setError(err.message);
      setIsSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setIsSubmitted(false);
    setError(null);
    setSuccess(false);
    if (onClearEdit) {
      onClearEdit();
    }
  };

  return (
    <div className="rr-wrap container-fluid pb-4">
      <div className="rr-card">
        <div className="rr-cardHeader">
          <p className="rr-kicker">Metadata Intake</p>
          <h5 className="rr-title">
            {editReportData ? "Update Power BI Report" : "Register Power BI Report"}
          </h5>
          <p className="rr-subtitle">
            Capture report identity, workspace mapping, and access settings.
          </p>
        </div>

        <div className="rr-content">
          <div className="rr-sidePanel">
            <div className="rr-sideBadge">Checklist</div>
            <ul className="rr-checklist">
              <li>Use valid GUID values for Report and Dataset IDs.</li>
              <li>Group ID should match the Power BI workspace.</li>
              <li>Enable RLS only when dataset has role definitions.</li>
            </ul>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="row g-3 rr-formGrid">
              <div className="col-md-6">
                <label htmlFor="reportName" className="form-label rr-label">
                  Report Name
                </label>
                <input
                  id="reportName"
                  name="reportName"
                  type="text"
                  className="form-control rr-input"
                  placeholder="Enter report name"
                  value={formData.reportName}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="col-md-6">
                <label htmlFor="reportId" className="form-label rr-label">
                  Report ID
                </label>
                <input
                  id="reportId"
                  name="reportId"
                  type="text"
                  className="form-control rr-input"
                  placeholder="Enter report id"
                  value={formData.reportId}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="col-md-6">
                <label htmlFor="groupId" className="form-label rr-label">
                  Group ID
                </label>
                <input
                  id="groupId"
                  name="groupId"
                  type="text"
                  className="form-control rr-input"
                  placeholder="Enter group id"
                  value={formData.groupId}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="col-md-6">
                <label htmlFor="datasetId" className="form-label rr-label">
                  Dataset ID
                </label>
                <input
                  id="datasetId"
                  name="datasetId"
                  type="text"
                  className="form-control rr-input"
                  placeholder="Enter dataset id"
                  value={formData.datasetId}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="col-md-6">
                <label htmlFor="reportUrl" className="form-label rr-label">
                  Report URL
                </label>
                <input
                  id="reportUrl"
                  name="reportUrl"
                  type="text"
                  className="form-control rr-input"
                  placeholder="https://app.powerbi.com/reportEmbed?reportId=...&groupId=..."
                  value={formData.reportUrl}
                  disabled
                  readOnly
                />
              </div>

              <div className="col-md-6">
                <label htmlFor="rlsEnabled" className="form-label rr-label">
                  RLS Enabled
                </label>
                <div className="rr-toggle" role="radiogroup" aria-label="RLS Enabled">
                  <label
                    className={`rr-toggleOption ${
                      formData.rlsEnabled === "true" ? "active" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="rlsEnabled"
                      value="true"
                      checked={formData.rlsEnabled === "true"}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    True
                  </label>
                  <label
                    className={`rr-toggleOption ${
                      formData.rlsEnabled === "false" ? "active" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="rlsEnabled"
                      value="false"
                      checked={formData.rlsEnabled === "false"}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    False
                  </label>
                </div>
              </div>

              <div className="col-12">
                <label
                  htmlFor="reportDescription"
                  className="form-label rr-label"
                >
                  Report Description
                </label>
                <textarea
                  id="reportDescription"
                  name="reportDescription"
                  className="form-control rr-input rr-textarea"
                  rows="4"
                  placeholder="Enter report description"
                  value={formData.reportDescription}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="d-flex gap-2 mt-4 rr-actions justify-content-end">
              <AppButton type="button" variant="secondary" onClick={handleReset} disabled={loading}>Clear</AppButton>
              <AppButton
                type="submit"
                variant="fill"
                disabled={loading || !formData.reportName.trim() || !formData.reportId.trim() || !formData.groupId.trim() || !formData.datasetId.trim() || !formData.reportDescription.trim()}
              >
                {loading ? "Registering..." : editReportData ? "Update" : "Register Report"}
              </AppButton>
            </div>
          </form>

          {error && (
            <div className="alert alert-danger mt-3 mb-0" role="alert">
              {error}
            </div>
          )}

          {success && (
            <div className="alert rr-alertSuccess mt-3 mb-0" role="alert">
              Report {editReportData ? "updated" : "registered"} successfully!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportRegistrationsFrom;
